import { useLayoutEffect, useRef } from 'react';
import { ChevronDown, Hammer, Plus, Trash2 } from 'lucide-react';

export const CUSTOM_POSITION = '__custom__';
export const montagePositionLabel = (row, positions) => row.position_label_edited
  ? row.position_label
  : positions.find(position => position.id === row.position)?.label || row.position_label || '';

const euro = cents => new Intl.NumberFormat('de-DE', {
  style: 'currency', currency: 'EUR',
}).format(cents / 100);
const MONTAGE_VAT_PERCENT = 19;
const MONTAGE_VAT_RATE = MONTAGE_VAT_PERCENT / 100;

export const montageRowsInvalid = (enabled, positions, rows) => enabled && (
  !rows.length || rows.some(row => {
    const price = String(row.net_price).trim();
    const positionLabel = montagePositionLabel(row, positions).trim();
    return (row.position !== CUSTOM_POSITION && !positions.some(position => position.id === row.position))
      || !positionLabel || positionLabel.length > 150
      || !row.installation_type.trim() || !row.description.trim()
      || !/^\d{1,7}(?:[.,]\d{1,2})?$/.test(price)
      || Number(price.replace(',', '.')) > 1000000;
  })
);

function GrowingTextarea({ value, ...props }) {
  const input = useRef(null);
  useLayoutEffect(() => {
    if (input.current) {
      input.current.style.height = 'auto';
      input.current.style.height = `${input.current.scrollHeight}px`;
    }
  }, [value]);
  return <textarea ref={input} rows={2} value={value} {...props} />;
}

export default function ClientOfferMontageSection({
  positions, enabled, rows, disabled, expanded, onToggle, onEnabled, onRowsChange,
}) {
  const netCents = rows.reduce((sum, row) => {
    const price = Number(String(row.net_price).replace(',', '.'));
    return sum + (Number.isFinite(price) ? Math.round(price * 100) : 0);
  }, 0);
  const vatCents = Math.round(netCents * MONTAGE_VAT_RATE);
  const invalid = montageRowsInvalid(enabled, positions, rows);
  const updateRow = (id, key, value) => onRowsChange(current => current.map(row => (
    row.id === id ? {
      ...row, [key]: value,
      ...(key === 'position_label' ? { position_label_edited: true } : {}),
    } : row
  )));
  const updatePosition = (id, value) => onRowsChange(current => current.map(row => (
    row.id === id ? {
      ...row, position: value,
      position_label: positions.find(position => position.id === value)?.label || '',
      position_label_edited: value === CUSTOM_POSITION,
    } : row
  )));
  const addRow = () => onRowsChange(current => [
    ...current,
    {
      id: Math.max(0, ...current.map(row => row.id)) + 1,
      position: CUSTOM_POSITION, position_label: '', position_label_edited: true,
      installation_type: '', description: '', net_price: '',
    },
  ]);

  return <section className="client-offers-card client-offers-step client-offers-montage" aria-labelledby="client-offers-montage-title">
    <div className="client-offers-step-heading">
      <span className="client-offers-number">6</span>
      <span className="client-offers-step-icon"><Hammer size={21} aria-hidden="true" /></span>
      <div className="client-offers-step-title"><h4 id="client-offers-montage-title">Montaż</h4>
        <p>Dodaj pozycje montażowe i wycenę w EUR na osobnej stronie oferty.</p></div>
      <label className="client-offers-toggle"><input type="checkbox" aria-label="Uwzględnij montaż"
        checked={enabled} disabled={disabled}
        onChange={event => onEnabled(event.target.checked)} />
        <span className="client-offers-toggle-track" aria-hidden="true" />
        <span>{enabled ? 'Włączone' : 'Wyłączone'}</span></label>
      <button className={`client-offers-collapse ${expanded ? 'open' : ''}`} type="button"
        aria-label={`${expanded ? 'Zwiń' : 'Rozwiń'} montaż`} aria-expanded={expanded}
        aria-controls="client-offers-montage-fields" onClick={onToggle}>
        <ChevronDown size={20} aria-hidden="true" />
      </button>
    </div>
    <div id="client-offers-montage-fields" hidden={!expanded}>
      {!positions.length && <p className="client-offers-pricing-hint">Nie znaleziono podpisów konstrukcji w ofercie. Możesz dodać własną pozycję montażu.</p>}
      {!enabled && <p className="client-offers-montage-idle">Włącz montaż, aby uzupełnić tabelę i dołączyć ją do PDF.</p>}
      {enabled && <div className="client-offers-montage-content">
        <div className="client-offers-montage-scroll"><table className="client-offers-montage-table">
          <thead><tr><th scope="col">Pozycja</th><th scope="col">Typ montażu</th>
            <th scope="col">Opis montażu</th><th scope="col">Cena netto (EUR)</th><th scope="col"><span className="client-offers-sr-only">Usuń</span></th></tr></thead>
          <tbody>{rows.map((row, index) => <tr key={row.id}>
            <td><select aria-label={`Pozycja montażu ${index + 1}`} value={row.position} disabled={disabled}
              onChange={event => updatePosition(row.id, event.target.value)}>
              {positions.map(position => <option key={position.id} value={position.id}>{position.label}</option>)}
              <option value={CUSTOM_POSITION}>Własna pozycja</option>
            </select><input type="text" className="client-offers-montage-position-name"
              aria-label={`Nazwa pozycji montażu ${index + 1}`} value={montagePositionLabel(row, positions)}
              maxLength={150} required disabled={disabled} placeholder="Nazwa pozycji na ofercie"
              onChange={event => updateRow(row.id, 'position_label', event.target.value)} /></td>
            <td><GrowingTextarea aria-label={`Typ montażu ${index + 1}`} value={row.installation_type}
              maxLength={120} required disabled={disabled}
              onChange={event => updateRow(row.id, 'installation_type', event.target.value)} /></td>
            <td><GrowingTextarea aria-label={`Opis montażu ${index + 1}`} value={row.description}
              maxLength={500} required disabled={disabled}
              onChange={event => updateRow(row.id, 'description', event.target.value)} /></td>
            <td><input type="number" aria-label={`Cena netto montażu ${index + 1}`} value={row.net_price}
              min="0" max="1000000" step="0.01" required disabled={disabled}
              onChange={event => updateRow(row.id, 'net_price', event.target.value)} /></td>
            <td><button type="button" className="client-offers-montage-remove" aria-label={`Usuń wiersz montażu ${index + 1}`}
              disabled={disabled} onClick={() => onRowsChange(current => current.filter(item => item.id !== row.id))}>
              <Trash2 size={17} aria-hidden="true" /></button></td>
          </tr>)}</tbody>
        </table></div>
        <button type="button" className="client-offers-montage-add" disabled={disabled || rows.length >= 200} onClick={addRow}>
          <Plus size={17} aria-hidden="true" /> Dodaj własną pozycję
        </button>
        <div className="client-offers-montage-totals" aria-label="Podsumowanie montażu">
          <div><span>Netto</span><strong>{euro(netCents)}</strong></div>
          <div><span>VAT ({MONTAGE_VAT_PERCENT}%)</span><strong>{euro(vatCents)}</strong></div>
          <div className="total"><span>Brutto</span><strong>{euro(netCents + vatCents)}</strong></div>
        </div>
        {invalid && <p className="client-offers-pricing-hint" role="alert">Uzupełnij nazwę pozycji, typ, opis i cenę netto każdego wiersza montażu.</p>}
      </div>}
    </div>
  </section>;
}
